import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Ban, Lock, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import ExpenseFormDialog from "@/components/ExpenseFormDialog";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const StatusBadge = ({ expense, t }) => {
  if (expense.status === "void")
    return <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 line-through">{t("status_void")}</span>;
  if (expense.needs_completion)
    return <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{t("status_needs_completion")}</span>;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      <Lock className="h-3 w-3" /> {t("status_posted")}
    </span>
  );
};

const HistoryTimeline = ({ history, t }) => (
  <ol className="space-y-3" data-testid="expense-history">
    {(history || []).map((h, i) => (
      <li key={i} className="border-l-2 border-orange-200 pl-3">
        <div className="text-[11px] font-semibold text-slate-700">
          {t(`expense_action_${h.action}`)} · <span className="font-normal text-slate-500">{h.by}</span>
        </div>
        <div className="text-[11px] text-slate-400">{new Date(h.at).toLocaleString("id-ID")}</div>
        {h.reason && <div className="mt-0.5 text-[11px] text-slate-600">“{h.reason}”</div>}
        {(h.changes || []).map((c, j) => (
          <div key={j} className="mt-0.5 text-[11px] text-slate-500 mono">
            {c.field}: {typeof c.from === "number" ? formatIDR(c.from) : c.from || "—"} → {typeof c.to === "number" ? formatIDR(c.to) : c.to || "—"}
          </div>
        ))}
      </li>
    ))}
  </ol>
);

export const ExpenseLedger = ({ deal, onChanged, quickAccount, onQuickHandled }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "editor";

  const [expenses, setExpenses] = useState([]);
  const [showVoid, setShowVoid] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [defaultAccount, setDefaultAccount] = useState("");
  const [detail, setDetail] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/deals/${deal.id}/expenses`, { params: { include_void: true } });
    setExpenses(res.data);
  }, [deal.id]);
  useEffect(() => { load(); }, [load]);

  const afterChange = async () => {
    await load();
    onChanged?.();
  };

  const visible = useMemo(
    () => expenses.filter((e) => showVoid || e.status !== "void"),
    [expenses, showVoid]
  );
  const postedTotal = useMemo(
    () => expenses.filter((e) => e.status === "posted").reduce((s, e) => s + e.total, 0),
    [expenses]
  );
  const perAccount = useMemo(() => {
    const map = {};
    expenses.filter((e) => e.status === "posted").forEach((e) => {
      (e.lines || []).forEach((l) => {
        const key = l.account_name ? `${l.account_name}|${l.account_code || ""}` : `${t("status_needs_completion")}|`;
        map[key] = (map[key] || 0) + Number(l.amount || 0);
      });
    });
    return Object.entries(map)
      .map(([k, v]) => ({ name: k.split("|")[0], code: k.split("|")[1], amount: v }))
      .sort((a, b) => b.amount - a.amount);
  }, [expenses, t]);

  const openNew = (accountCode = "") => { setEditTarget(null); setDefaultAccount(accountCode); setFormOpen(true); };
  const openEdit = (expense) => { setEditTarget(expense); setDefaultAccount(""); setFormOpen(true); };

  useEffect(() => {
    if (!quickAccount) return;
    setEditTarget(null);
    setDefaultAccount(quickAccount.code);
    setFormOpen(true);
    onQuickHandled?.();
  }, [quickAccount, onQuickHandled]);

  const confirmVoid = async () => {
    if (!voidReason.trim()) { toast.error(t("field_required")); return; }
    try {
      await api.post(`/expenses/${voidTarget.id}/void`, { reason: voidReason.trim() });
      toast.success(t("expense_voided"));
      setVoidTarget(null);
      setVoidReason("");
      await afterChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-4" data-testid="expense-ledger">
      {!canWrite && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="expense-readonly-banner">
          <Eye className="h-3.5 w-3.5" /> {t("view_only_mode")}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("expenses")}</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <input type="checkbox" checked={showVoid} data-testid="expense-show-void"
                onChange={(e) => setShowVoid(e.target.checked)} />
              {t("show_void")}
            </label>
            {canWrite && (
              <Button size="sm" data-testid="expense-add-btn" onClick={() => openNew()}
                className="h-7 gap-1 bg-orange-500 text-xs hover:bg-orange-600">
                <Plus className="h-3 w-3" /> {t("record_expense")}
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-3 py-2">{t("number")}</th>
                <th className="px-3 py-2">{t("date")}</th>
                <th className="px-3 py-2">{t("beneficiary")}</th>
                <th className="px-3 py-2">{t("expense_account")}</th>
                <th className="px-3 py-2">{t("description")}</th>
                <th className="px-3 py-2 text-right">{t("total")}</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">{t("no_expenses")}</td></tr>
              )}
              {visible.map((e) => {
                const first = (e.lines || [])[0] || {};
                const extra = (e.lines || []).length - 1;
                return (
                  <tr key={e.id} data-testid={`expense-row-${e.id}`}
                    className={`cursor-pointer border-b border-slate-100 hover:bg-orange-50/30 ${e.status === "void" ? "text-slate-400 line-through" : ""}`}
                    onClick={() => setDetail(e)}>
                    <td className="px-3 py-2 mono font-semibold text-slate-700">{e.number}</td>
                    <td className="whitespace-nowrap px-3 py-2">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2">{e.beneficiary_name || <span className="text-amber-600">—</span>}</td>
                    <td className="px-3 py-2">
                      {first.account_name || <span className="text-amber-600">—</span>}
                      {first.account_code && <div className="text-[10px] text-slate-400 mono">{first.account_code}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {first.description || "—"}
                      {extra > 0 && <span className="ml-1 text-[10px] text-slate-400">+{extra} {t("lines_suffix")}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right mono font-semibold">{formatIDR(e.total)}</td>
                    <td className="px-3 py-2"><StatusBadge expense={e} t={t} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right" onClick={(ev) => ev.stopPropagation()}>
                      {canWrite && e.status !== "void" && (
                        <>
                          <button data-testid={`expense-edit-${e.id}`} onClick={() => openEdit(e)}
                            className="p-1.5 text-slate-400 hover:text-slate-900"><Pencil className="h-3.5 w-3.5" /></button>
                          <button data-testid={`expense-void-${e.id}`} onClick={() => { setVoidTarget(e); setVoidReason(""); }}
                            className="p-1.5 text-slate-400 hover:text-rose-600"><Ban className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-600" colSpan={5}>{t("posted_expense_total")}</td>
                <td className="px-3 py-2 text-right mono font-bold text-slate-900" data-testid="expense-posted-total">{formatIDR(postedTotal)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {perAccount.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="expense-account-summary">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t("summary_per_account")}</div>
          <div className="space-y-2">
            {perAccount.map((a) => (
              <div key={`${a.name}-${a.code}`} data-testid={`account-summary-${a.code || "none"}`}>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-slate-700">{a.name} {a.code && <span className="text-[10px] text-slate-400 mono">{a.code}</span>}</span>
                  <span className="mono font-semibold text-slate-900">{formatIDR(a.amount)}</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full bg-orange-400"
                    style={{ width: `${postedTotal ? (a.amount / postedTotal) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ExpenseFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        dealId={deal.id}
        expense={editTarget}
        defaultAccountCode={defaultAccount}
        onSaved={afterChange}
      />

      {/* detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto crm-scroll" data-testid="expense-detail-dialog">
          <DialogHeader>
            <DialogTitle className="mono">{detail?.number}</DialogTitle>
            <DialogDescription className="sr-only">{t("expense_detail")}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-400">{t("date")}</span><div className="font-semibold text-slate-800">{fmtDate(detail.date)}</div></div>
                <div><span className="text-slate-400">{t("beneficiary")}</span><div className="font-semibold text-slate-800">{detail.beneficiary_name || "—"}</div></div>
                <div><span className="text-slate-400">{t("reference")}</span><div className="font-semibold text-slate-800">{detail.reference || "—"}</div></div>
                <div><span className="text-slate-400">{t("deal")}</span><div className="font-semibold text-slate-800">{deal.title}</div></div>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="w-full">
                  <tbody>
                    {(detail.lines || []).map((l, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          {l.account_name || <span className="text-amber-600">{t("status_needs_completion")}</span>}
                          {l.account_code && <span className="ml-1 text-[10px] text-slate-400 mono">{l.account_code}</span>}
                          {l.description && <div className="text-slate-500">{l.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-right mono">{formatIDR(l.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td className="px-3 py-2 font-bold">{t("total")}</td>
                      <td className="px-3 py-2 text-right mono font-bold">{formatIDR(detail.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  <FileText className="h-3 w-3" /> {t("history")}
                </div>
                <HistoryTimeline history={detail.history} t={t} />
              </div>
            </div>
          )}
          <DialogFooter>
            {canWrite && detail?.status !== "void" && (
              <>
                <Button variant="outline" data-testid="expense-detail-edit"
                  onClick={() => { const target = detail; setDetail(null); openEdit(target); }}>
                  <Pencil className="mr-1 h-3.5 w-3.5" /> {t("edit_expense")}
                </Button>
                <Button variant="outline" data-testid="expense-detail-void"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={() => { setVoidTarget(detail); setVoidReason(""); setDetail(null); }}>
                  <Ban className="mr-1 h-3.5 w-3.5" /> {t("void_expense")}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* void confirm */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => !o && setVoidTarget(null)}>
        <DialogContent className="max-w-md" data-testid="expense-void-dialog">
          <DialogHeader>
            <DialogTitle>{t("void_expense")}</DialogTitle>
            <DialogDescription className="text-xs">{t("void_warning").replace("{number}", voidTarget?.number || "")}</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">{t("void_reason")}*</Label>
            <Input value={voidReason} data-testid="expense-void-reason" onChange={(e) => setVoidReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)}>{t("cancel")}</Button>
            <Button onClick={confirmVoid} data-testid="expense-void-confirm" className="bg-rose-600 hover:bg-rose-700">
              {t("void_expense")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseLedger;
